import {useQueries} from '@tanstack/react-query';
import axios, {AxiosError} from 'axios';
import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {Navigate, Route, Routes, useNavigate, useParams} from 'react-router';
import {useLocation} from 'react-router-dom';
import {SELECTED_COMPANY_UUID_KEY} from 'constants/local-storage-keys';
import AppStartLoader from 'ui/@_components/app-start-loader';
import {
  useCurrentCompanyState,
  useCurrentCompanyUuid,
} from 'ui/@atoms/current-company';
import TwoPanePage from 'ui/@_components/two-pane-page';
import lazyPage from 'ui/@hoc/lazy-page';
import {routeNames, routeParams} from '../../constants/route-names';
import {InstillAPIError} from '../../repositories/instill/axios-instance';
import {fetchMeeting} from 'repositories/instill/queries/fetch-meeting';
import SelectCompanyContextProvider from '../@contexts/company-selector-context';
import OnboardingGuard from './@components/onboarding-guard';
import PublicMeetingShell from './@components/public-meeting-shell';
import RequireAuth from './@components/require-auth';
import useFetchAndUpsertUser from './@hooks/use-fetch-and-upsert-user';
import useWhiteLabellingTheme from './@hooks/use-white-labelling-theme';
import useSyncLanguage from 'ui/@hooks/use-sync-language';
import CreatingAccount from './@components/creating-account';
import {EmailNotVerifiedException} from 'repositories/instill/mutations/upsert-auth-user';
import PageTemplate from 'ui/@_components/page-template/page-template';

const AUTH0_EMAIL_VERIFICATION_CALLBACK_URL = '/auth-callback/email-verified';

const Application = lazyPage(
  () => import('../app/application'),
  <AppStartLoader />
);
const SelectCompany = lazyPage(() => import('../select-company')); // done
const Logout = lazyPage(() => import('../logout')); // done
const Invite = lazyPage(() => import('../invite')); // done
const StoryBook = lazyPage(() => import('../@story-book/story-book'));
const RegisterCompany = lazyPage(() => import('../register-company'));
const Candidates = lazyPage(() => import('../candidates-v2')); // done
const DemoAccount = lazyPage(() => import('../demo-account'));
const AccountLinked = lazyPage(
  () => import('./@components/auth-feedback/@components/account-linked')
);
const AuthFeedback = lazyPage(() => import('./@components/auth-feedback'));
const VerifyYourEmail = lazyPage(() => import('../verify-your-email')); // done
const PageNotFound = lazyPage(() => import('../page-not-found'));
const MeetingsShow = lazyPage(
  () => import('../app/flow/meetings/show/meetings-show')
);
const CandidateReport = lazyPage(
  () =>
    import(
      '../app/candidate-calibration/job-positions/show/report/@components/main-content'
    )
);
const EmailVerified = lazyPage(() => import('../email-verified'));
const OAuthCallback = lazyPage(() => import('../oauth-callback'));

const PublicMeetingRoute = ({
  currentAuthUser,
}: {
  currentAuthUser: ReturnType<typeof useFetchAndUpsertUser>['currentAuthUser'];
}) => {
  const {meetingId} = useParams<{meetingId: string}>();
  const navigate = useNavigate();
  const [currentCompany, setCurrentCompany] = useCurrentCompanyState();
  const currentCompanyUuid = useCurrentCompanyUuid();

  const isLoggedIn = !!currentAuthUser;
  const companies = currentAuthUser?.companies ?? [];
  const canProbe = isLoggedIn && !!meetingId && companies.length > 0;

  // The public share route lives outside SelectCompanyContextProvider, so the
  // currentCompany recoil atom is empty for users arriving directly. We probe
  // the authenticated `/companies/:uuid/meetings/:id` endpoint for every
  // company the user belongs to in parallel; if any one returns data the user
  // owns the meeting and we redirect them to the internal URL.
  const probes = useQueries({
    queries: companies.map((company) => ({
      queryKey: ['public-meeting-ownership-probe', company.uuid, meetingId],
      queryFn: () =>
        fetchMeeting({
          companyUuid: company.uuid,
          meetingUuid: meetingId,
        }).catch(() => undefined),
      enabled: canProbe,
      retry: false,
      staleTime: 30_000,
    })),
  });

  const ownerIndex = probes.findIndex((p) => p.data);
  const ownerCompany = ownerIndex >= 0 ? companies[ownerIndex] : null;
  const isProbing =
    canProbe && !ownerCompany && probes.some((p) => p.isLoading);

  useEffect(() => {
    if (!ownerCompany || !meetingId) return;

    // Persist the selection so the internal route renders without a company
    // picker round-trip, then navigate to the authenticated meeting URL.
    if (currentCompany?.uuid !== ownerCompany.uuid) {
      setCurrentCompany(ownerCompany);
      localStorage.setItem(SELECTED_COMPANY_UUID_KEY, ownerCompany.uuid);
    }

    navigate(
      `/${routeNames.applicationRoute}/${routeNames.senseRoute}/${routeNames.meetingsRoute}/${meetingId}`,
      {replace: true}
    );
  }, [ownerCompany, meetingId, currentCompany, setCurrentCompany, navigate]);

  if (isProbing || ownerCompany) {
    return <AppStartLoader />;
  }

  // Authenticated user viewing a meeting that isn't theirs: keep them in the
  // full v2 app chrome (the Flow MeetingsShow renders its own
  // ApplicationContentWrapper internally). Suppress share/get-instill CTAs
  // (handled inside FlowSubheader via the public-route flag). Sidebar/profile
  // depend on a selected company; without one we fall back to the anonymous
  // shell to avoid crashing on `useSafeCurrentCompanyUuid`.
  if (isLoggedIn && currentCompanyUuid) {
    return (
      <PageTemplate v2Background hideNavigation>
        <MeetingsShow />
      </PageTemplate>
    );
  }

  // Anonymous viewer: slim public shell with logo + Get Instill CTA.
  return (
    <PublicMeetingShell>
      <MeetingsShow />
    </PublicMeetingShell>
  );
};

const ApplicationRoutes = () => {
  const {t} = useTranslation();
  const location = useLocation();
  const {currentAuthUser, isLoading, error, isAccountLinked, userProviders} =
    useFetchAndUpsertUser();

  useWhiteLabellingTheme();
  useSyncLanguage();

  if (isLoading) {
    return <AppStartLoader />;
  }

  if (isAccountLinked) {
    return <AccountLinked providers={userProviders} />;
  }

  if (axios.isAxiosError(error)) {
    const serverError = error as AxiosError<InstillAPIError>;
    return <AuthFeedback errorPayload={serverError.response?.data.payload} />;
  } else if (error instanceof EmailNotVerifiedException) {
    if (!location.pathname.includes(AUTH0_EMAIL_VERIFICATION_CALLBACK_URL)) {
      return <VerifyYourEmail email={error.payload.email} />;
    }
  } else if (error instanceof Error) {
    return <div>{t('error', {message: error.message})}</div>;
  } else if (typeof error === 'string') {
    return <div>{t('error', {message: error})}</div>;
  } else if (error) {
    return <div>{t('error', {message: t('unknown')})}</div>;
  }

  return (
    <div>
      <Routes>
        <Route
          path={`/${routeNames.candidatesRoute}/*`}
          element={
            <PageTemplate hideNavigation v2Background>
              <Candidates />
            </PageTemplate>
          }
        />

        <Route element={<RequireAuth user={currentAuthUser} />}>
          <Route
            path="/"
            element={<Navigate replace to={routeNames.applicationRoute} />}
          />

          <Route
            path={`${routeNames.applicationRoute}/*`}
            element={
              <SelectCompanyContextProvider>
                <OnboardingGuard>
                  <CreatingAccount />

                  <Application />
                </OnboardingGuard>
              </SelectCompanyContextProvider>
            }
          />

          <Route path={`/${routeNames.logoutRoute}`} element={<Logout />} />

          {/* As of August 1st, company registration is temporarily disabled. Companies must be created through admin portal */}
          <Route
            path={`/${routeNames.registerCompanyRoute}/`}
            element={
              <TwoPanePage>
                <RegisterCompany />
              </TwoPanePage>
            }
          />

          <Route
            path={`/${routeNames.selectCompany}/`}
            element={
              <SelectCompanyContextProvider>
                <SelectCompany />
              </SelectCompanyContextProvider>
            }
          />

          <Route
            path="/story-book/*"
            element={
              <SelectCompanyContextProvider>
                <StoryBook />
              </SelectCompanyContextProvider>
            }
          />
        </Route>

        <Route
          path={`/invite/:${routeParams.inviteTokenParam}`}
          element={<Invite />}
        />

        <Route
          path={`/${routeNames.unAuthorizedMeetingRoute}/:${routeParams.meetingIdParam}`}
          element={<PublicMeetingRoute currentAuthUser={currentAuthUser} />}
        />

        <Route
          path={`/${routeNames.candidatesRoute}/${routeNames.candidateResult}/:${routeParams.candidateIdParam}`}
          element={<CandidateReport />}
        />

        <Route
          path={`/${routeNames.demoAccountRoute}/*`}
          element={<DemoAccount />}
        />

        <Route
          path={AUTH0_EMAIL_VERIFICATION_CALLBACK_URL}
          element={<EmailVerified />}
        />

        <Route path="*" element={<Navigate replace to="/page-not-found" />} />

        <Route path="/page-not-found" element={<PageNotFound />} />

        <Route
          path={`${routeNames.oAuthCallbackRoute}/*`}
          element={
            <SelectCompanyContextProvider>
              <OAuthCallback />
            </SelectCompanyContextProvider>
          }
        />
      </Routes>
    </div>
  );
};

export default ApplicationRoutes;
